import { useState, useRef, useCallback, useEffect } from 'react';
import { AudioPlayer } from './lib/play/AudioPlayer.js';
import { ChatHistoryManager } from './lib/util/ChatHistoryManager.js';
import { WebSocketWrapper } from './lib/ws/WebSocketWrapper.js';
import { publishToast } from '../../utils/toastUtils.js';

// Constants
const TARGET_SAMPLE_RATE = 16000;
const SESSION_TIMEOUT_MS = 7 * 60 * 1000;
const SESSION_WARNING_MS = 6 * 60 * 1000;

const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

// Helper functions
function arrayBufferToBase64(buffer) {
  const binary = [];
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary.push(String.fromCharCode(bytes[i]));
  }
  return btoa(binary.join(''));
}

function base64ToFloat32Array(base64String) {
  const binaryString = window.atob(base64String);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const int16Array = new Int16Array(bytes.buffer);
  const float32Array = new Float32Array(int16Array.length);
  for (let i = 0; i < int16Array.length; i++) {
    float32Array[i] = int16Array[i] / 32768.0;
  }
  return float32Array;
}

/**
 * Custom hook for managing voice streaming functionality.
 * Handles WebSocket connection, audio capture, playback, and session management.
 *
 * @param {Object} options
 * @param {string} options.wsUrl - WebSocket URL for the voice connection
 * @param {string} options.apiBaseUrl - Base URL for API calls
 * @param {Object} options.config - Configuration object containing voice settings
 * @param {Function} options.onUserAudioLevel - Callback for user audio level updates (0-1)
 * @param {Function} options.onAssistantAudioLevel - Callback for assistant audio level updates (0-1)
 * @param {Function} options.onSpeechStart - Callback when assistant starts speaking
 * @param {Function} options.onSpeechEnd - Callback when assistant stops speaking
 * @param {Function} options.onAudioReady - Callback when audio stream is ready
 */
export function useVoiceStreaming({
  wsUrl = null,
  config,
  onUserAudioLevel,
  onAssistantAudioLevel,
  onSpeechStart,
  onSpeechEnd,
  onAudioReady
}) {
  // State
  const [isStreaming, setIsStreaming] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [waitingForAssistant, setWaitingForAssistant] = useState(false);
  const [waitingForUser, setWaitingForUser] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [sessionRenewing, setSessionRenewing] = useState(null);
  const [settingsDisabled, setSettingsDisabled] = useState(false);

  // Refs
  const socketRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioStreamRef = useRef(null);
  const processorRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const sessionInitializedRef = useRef(false);
  const manualDisconnectRef = useRef(false);
  const samplingRatioRef = useRef(1);
  const roleRef = useRef(null);
  const displayAssistantTextRef = useRef(false);
  const pendingToolUsesRef = useRef([]);
  const isStreamingRef = useRef(false);

  // Session timeout refs
  const sessionTimeoutTimerRef = useRef(null);
  const sessionWarningTimerRef = useRef(null);

  // Chat history manager ref
  const chatHistoryManagerRef = useRef(null);
  const chatRef = useRef({ history: [] });

  // Keep isStreamingRef in sync
  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Initialize socket
  useEffect(() => {
    const defaultWsUrl = `${process.env.REACT_APP_WEBSOCKET_URL}/voice`;
    socketRef.current = new WebSocketWrapper(wsUrl || defaultWsUrl);
    audioPlayerRef.current = new AudioPlayer();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [wsUrl]);

  // Initialize chat history manager
  useEffect(() => {
    chatHistoryManagerRef.current = ChatHistoryManager.getInstance(
      chatRef,
      (newChat) => {
        chatRef.current = { ...newChat };
        setChatHistory([...newChat.history]);
      }
    );
  }, []);

  // Session timer management
  const clearSessionTimers = useCallback(() => {
    if (sessionTimeoutTimerRef.current) {
      clearTimeout(sessionTimeoutTimerRef.current);
      sessionTimeoutTimerRef.current = null;
    }
    if (sessionWarningTimerRef.current) {
      clearTimeout(sessionWarningTimerRef.current);
      sessionWarningTimerRef.current = null;
    }
  }, []);

  const handleSessionTimeout = useCallback(async () => {
    console.log('Session timeout - auto-renewing...');
    setSessionWarning(false);
    setSessionRenewing('renewing');

    try {
      const wasStreaming = isStreamingRef.current;

      if (sessionInitializedRef.current) {
        await new Promise((resolve) => {
          const timeout = setTimeout(() => resolve(), 5000);
          socketRef.current.once('sessionClosed', () => {
            clearTimeout(timeout);
            resolve();
          });
          socketRef.current.emit('stopAudio');
        });
      }

      sessionInitializedRef.current = false;
      await new Promise(resolve => setTimeout(resolve, 300));

      if (wasStreaming) {
        // Will be re-initialized by startStreaming flow
        setSessionRenewing('success');
      } else {
        setSessionRenewing('ended');
      }

      setTimeout(() => setSessionRenewing(null), 2500);
    } catch (error) {
      console.error('Failed to renew session:', error);
      setSessionRenewing('error');
    }
  }, []);

  const startSessionTimers = useCallback(() => {
    clearSessionTimers();

    sessionWarningTimerRef.current = setTimeout(() => {
      setSessionWarning(true);
    }, SESSION_WARNING_MS);

    sessionTimeoutTimerRef.current = setTimeout(() => {
      handleSessionTimeout();
    }, SESSION_TIMEOUT_MS);
  }, [clearSessionTimers, handleSessionTimeout]);

  // Audio initialization
  const initAudio = useCallback(async () => {
    try {
      audioStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true }
        }
      });

      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = isFirefox
          ? new AudioContext()
          : new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
      }

      samplingRatioRef.current = audioContextRef.current.sampleRate / TARGET_SAMPLE_RATE;
      await audioPlayerRef.current.start(config.outputSampleRate, config.audioBufferMs);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      throw error;
    }
  }, [config.outputSampleRate, config.audioBufferMs]);

  const getPrompt = async (presetName) => {
    try {
      const response = await fetch(`/prompts/${presetName}.md`);
      if (response.ok) {
        const content = await response.text();
        return content;
      }
    } catch (error) {
      console.error('Failed to load prompt preset:', error);
    }
  }

  // Session initialization
  const initializeSession = useCallback(async () => {
    if (sessionInitializedRef.current) return;

    try {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Connection timeout')), 10000);

        socketRef.current.emit('initializeConnection', {
          region: config.awsRegion,
          inferenceConfig: {
            maxTokens: config.maxTokens,
            temperature: config.temperature,
            topP: config.topP
          },
          turnDetectionConfig: {
            endpointingSensitivity: config.responseTiming
          },
          enabledTools: config.enabledTools,
          applicationId: localStorage.getItem('selectedConfigId')
        }, (ack) => {
          clearTimeout(timeout);
          if (ack?.success) resolve();
          else reject(new Error(ack?.error || 'Connection failed'));
        });
      });

      await audioPlayerRef.current.setSampleRate(config.outputSampleRate);

      socketRef.current.emit('promptStart', {
        voiceId: config.voiceId,
        outputSampleRate: config.outputSampleRate
      });

      socketRef.current.emit('systemPrompt', {
        content: (prompt => {
          Object.keys(config.systemPrompt ?? {}).forEach(key => {
            prompt = prompt.replaceAll(`\${${key}}`, config.systemPrompt[key])
          });
          return prompt;
        })(await getPrompt('default')),
        voiceId: config.voiceId
      });

      socketRef.current.emit('audioStart');
      socketRef.current.emit('textInput', { content: 'initialize conversation' })

      sessionInitializedRef.current = true;
      startSessionTimers();
    } catch (error) {
      console.error('Failed to initialize session:', error);
      throw error;
    }
  }, [config, startSessionTimers]);

  // Stop streaming
  const stopStreaming = useCallback(() => {
    if (!isStreamingRef.current) return;

    setIsStreaming(false);
    clearSessionTimers();
    setSessionWarning(false);

    if (processorRef.current) {
      processorRef.current.disconnect();
      sourceNodeRef.current?.disconnect();
      processorRef.current = null;
      sourceNodeRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    audioPlayerRef.current.bargeIn();
    socketRef.current.emit('stopAudio');
    chatHistoryManagerRef.current.endTurn();

    sessionInitializedRef.current = false;
    manualDisconnectRef.current = true;
    socketRef.current.disconnect();

    setSettingsDisabled(false);
    onSpeechEnd?.();
  }, [clearSessionTimers, onSpeechEnd]);

  // Start streaming
  const startStreaming = useCallback(async () => {
    if (isStreamingRef.current) return;

    try {
      chatHistoryManagerRef.current.clearHistory();
      setChatHistory([]);

      if (!socketRef.current.connected) {
        socketRef.current.connect();
        await new Promise((resolve) => {
          if (socketRef.current.connected) resolve();
          else socketRef.current.once('connect', resolve);
        });
      }

      if (!audioStreamRef.current || !audioContextRef.current || audioContextRef.current.state === 'closed') {
        await initAudio();
      }

      if (!audioPlayerRef.current.initialized) {
        await audioPlayerRef.current.start(config.outputSampleRate, config.audioBufferMs);
      }

      if (!sessionInitializedRef.current) {
        await initializeSession();
      }

      sourceNodeRef.current = audioContextRef.current.createMediaStreamSource(audioStreamRef.current);

      if (audioContextRef.current.createScriptProcessor) {
        processorRef.current = audioContextRef.current.createScriptProcessor(512, 1, 1);

        processorRef.current.onaudioprocess = (e) => {
          if (!isStreamingRef.current) return;

          const inputData = e.inputBuffer.getChannelData(0);
          const numSamples = Math.round(inputData.length / samplingRatioRef.current);
          const pcmData = isFirefox ? new Int16Array(numSamples) : new Int16Array(inputData.length);

          let sum = 0;
          for (let i = 0; i < inputData.length; i++) {
            sum += inputData[i] * inputData[i];
          }
          const rms = Math.sqrt(sum / inputData.length);
          const audioLevel = Math.min(1, rms * 3);
          onUserAudioLevel?.(audioLevel);

          if (isFirefox) {
            for (let i = 0; i < numSamples; i++) {
              pcmData[i] = Math.max(-1, Math.min(1, inputData[Math.floor(i * samplingRatioRef.current)])) * 0x7FFF;
            }
          } else {
            for (let i = 0; i < inputData.length; i++) {
              pcmData[i] = Math.max(-1, Math.min(1, inputData[i])) * 0x7FFF;
            }
          }

          const base64Data = arrayBufferToBase64(pcmData.buffer);
          socketRef.current.emit('audioInput', base64Data);
        };

        sourceNodeRef.current.connect(processorRef.current);
        processorRef.current.connect(audioContextRef.current.destination);
      }

      setIsStreaming(true);
      setSettingsDisabled(true);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  }, [config, initAudio, initializeSession, onUserAudioLevel]);

  // Socket event handlers
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleContentStart = (data) => {
      if (data.type === 'TEXT') {
        roleRef.current = data.role;
        if (data.role === 'ASSISTANT') {
          setWaitingForAssistant(false);
          let isSpeculative = false;
          try {
            if (data.additionalModelFields) {
              const additionalFields = JSON.parse(data.additionalModelFields);
              isSpeculative = additionalFields.generationStage === 'SPECULATIVE';
              displayAssistantTextRef.current = isSpeculative;
            } else {
              displayAssistantTextRef.current = false;
            }
          } catch (e) {
            console.error('Error parsing additionalModelFields:', e);
          }
        }
      }
    };

    const handleTextOutput = (data) => {
      if (roleRef.current === 'USER') {
        setWaitingForUser(false);
        chatHistoryManagerRef.current.addTextMessage({ role: data.role, message: data.content });
        setWaitingForAssistant(true);
      } else if (roleRef.current === 'ASSISTANT' && displayAssistantTextRef.current) {
        chatHistoryManagerRef.current.addTextMessage({ role: data.role, message: data.content });
      }
    };

    const handleAudioOutput = (data) => {
      if (data.content) {
        try {
          onSpeechStart?.();

          const audioData = base64ToFloat32Array(data.content);
          audioPlayerRef.current.playAudio(audioData);

          let sum = 0;
          for (let i = 0; i < audioData.length; i++) {
            sum += audioData[i] * audioData[i];
          }
          const rms = Math.sqrt(sum / audioData.length);
          const audioLevel = Math.min(1, rms * 3);
          onAssistantAudioLevel?.(audioLevel);
        } catch (error) {
          console.error('Error processing audio data:', error);
        }
      }
    };

    const handleContentEnd = (data) => {
      if (data.type === 'TEXT') {
        if (roleRef.current === 'USER') {
          setWaitingForUser(false);
          setWaitingForAssistant(true);
        } else if (roleRef.current === 'ASSISTANT') {
          setWaitingForAssistant(false);
        }

        if (data.stopReason?.toUpperCase() === 'END_TURN') {
          pendingToolUsesRef.current = [];
          chatHistoryManagerRef.current.endTurn();
        } else if (data.stopReason?.toUpperCase() === 'INTERRUPTED') {
          audioPlayerRef.current.bargeIn();
          chatHistoryManagerRef.current.markLastAssistantInterrupted();
          onSpeechEnd?.();
        }
      } else if (data.type === 'AUDIO') {
        // Signal that audio content has ended
        onSpeechEnd?.();
      }
    };

    const handleBargeIn = () => {
      audioPlayerRef.current.bargeIn();
      chatHistoryManagerRef.current.markLastAssistantInterrupted();
      onSpeechEnd?.();
    };

    const handleToolUse = (data) => {
      publishToast(data.toolName, ((content) => {
        try {
          return JSON.parse(content).message || content
        } catch (e) {
          return content;
        }
      })(data.content), 'smart_toy');
      setWaitingForAssistant(false);

      let inputData = data.content;
      if (typeof inputData === 'string') {
        try {
          inputData = JSON.parse(inputData);
        } catch (e) { }
      }

      const toolData = {
        type: 'tool',
        toolUseId: data.toolUseId,
        toolName: data.toolName,
        input: inputData || {},
        startTime: Date.now(),
        status: 'running'
      };

      pendingToolUsesRef.current.push(toolData);
      chatHistoryManagerRef.current.addToolUse(toolData);
    };

    const handleToolResult = (data) => {
      publishToast('Tool Result', data.result.messageID);

      const toolIndex = pendingToolUsesRef.current.findIndex(t => t.toolUseId === data.toolUseId);
      if (toolIndex !== -1) {
        const tool = pendingToolUsesRef.current[toolIndex];
        tool.output = data.result;
        tool.endTime = Date.now();
        tool.elapsed = data.executionTimeMs || (tool.endTime - tool.startTime);
        tool.status = 'completed';

        chatHistoryManagerRef.current.updateToolResult(data.toolUseId, tool);
      }

      setWaitingForAssistant(true);
    };

    const handleStreamComplete = () => {
      if (isStreamingRef.current) stopStreaming();
    };

    const handleConnect = () => {
      sessionInitializedRef.current = false;
    };

    const handleDisconnect = () => {
      if (manualDisconnectRef.current) {
        manualDisconnectRef.current = false;
      }
      sessionInitializedRef.current = false;
      setWaitingForUser(false);
      setWaitingForAssistant(false);
    };

    const handleError = (error) => {
      console.error('Server error:', error);
      setWaitingForUser(false);
      setWaitingForAssistant(false);

      if (error?.source === 'responseStream' && error?.details) {
        if (isStreamingRef.current) stopStreaming();
      }

      if (error?.message === 'No active session for audio input' && isStreamingRef.current) {
        stopStreaming();
      }
    };

    const handleAudioReady = () => {
      onAudioReady?.();
    };

    socket.on('contentStart', handleContentStart);
    socket.on('textOutput', handleTextOutput);
    socket.on('audioOutput', handleAudioOutput);
    socket.on('contentEnd', handleContentEnd);
    socket.on('bargeIn', handleBargeIn);
    socket.on('toolUse', handleToolUse);
    socket.on('toolResult', handleToolResult);
    socket.on('streamComplete', handleStreamComplete);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('error', handleError);
    socket.on('audioReady', handleAudioReady);

    return () => {
      socket.off('contentStart', handleContentStart);
      socket.off('textOutput', handleTextOutput);
      socket.off('audioOutput', handleAudioOutput);
      socket.off('contentEnd', handleContentEnd);
      socket.off('bargeIn', handleBargeIn);
      socket.off('toolUse', handleToolUse);
      socket.off('toolResult', handleToolResult);
      socket.off('streamComplete', handleStreamComplete);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('error', handleError);
      socket.off('audioReady', handleAudioReady);
    };
  }, [stopStreaming, onAssistantAudioLevel, onSpeechStart, onSpeechEnd, onAudioReady]);

  return {
    // State
    isStreaming,
    chatHistory,
    waitingForAssistant,
    waitingForUser,
    sessionWarning,
    sessionRenewing,
    settingsDisabled,

    // Actions
    startStreaming,
    stopStreaming,

    // Utilities
    clearChatHistory: () => {
      chatHistoryManagerRef.current.clearHistory();
      setChatHistory([]);
    }
  };
}
